// ============================================================================
// STORE.PACKETS.JS — Pre-Referral Packet client API (Phase 1)
//
// All mutations and PHI document access go through Cloud Functions. The client
// reads only the Never-PHI packet root collection at:
//   tenants/{tenantId}/preReferralPackets/{packetId}
//
// Client NEVER reads /phi/documents/** directly — server-only.
//
// Status enums:
//   Packet:           INBOX | DRAFTING_REFERRAL | COMPLETED | FAILED
//   Packet document:  UPLOADING | READY | PROMOTING | PROMOTED | FAILED
// ============================================================================

import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  doc,
  getDoc,
  onSnapshot,
} from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { PATHS } from './firestorePaths.js';
import { getFirebaseApp } from './store.firebase.js';

// ── Status constants ─────────────────────────────────────────────────────────

export const PACKET_STATUS = Object.freeze({
  INBOX:              'INBOX',
  DRAFTING_REFERRAL:  'DRAFTING_REFERRAL',
  COMPLETED:          'COMPLETED',
  FAILED:             'FAILED',
  DELETED:            'DELETED',
});

export const PACKET_DOCUMENT_STATUS = Object.freeze({
  UPLOADING: 'UPLOADING',
  READY:     'READY',
  PROMOTING: 'PROMOTING',
  PROMOTED:  'PROMOTED',
  FAILED:    'FAILED',
});

export const PACKET_DOCUMENT_TYPES = Object.freeze([
  'LABS', 'IMAGING', 'PDF', 'PHOTO', 'OTHER',
]);

// ── Phase 2A: OCR extraction status (read from the PHI document manifest) ────
export const OCR_EXTRACTION_STATUS = Object.freeze({
  NOT_RUN:      'NOT_RUN',
  QUEUED:       'QUEUED',
  PROCESSING:   'PROCESSING',
  COMPLETED:    'COMPLETED',
  FAILED:       'FAILED',
  NEEDS_REVIEW: 'NEEDS_REVIEW',
});

// Statuses where a job is still in flight — UI should poll while in these.
export const OCR_IN_FLIGHT_STATUSES = Object.freeze([
  OCR_EXTRACTION_STATUS.QUEUED,
  OCR_EXTRACTION_STATUS.PROCESSING,
]);

// ── Phase 2B-4: packet preparation (readiness) for the Gate ──────────────────
// Mirrors the server PREPARATION_STATUS. NEEDS_PREPARATION is a client-only
// pre-check signal ("there is NOT_RUN work to trigger"); the server never
// returns it (it always enqueues or resolves to a terminal/partial status).
export const PACKET_PREPARATION_STATUS = Object.freeze({
  READY:                     'READY',
  NEEDS_PREPARATION:         'NEEDS_PREPARATION',
  PREPARING:                 'PREPARING',
  PARTIAL_READY:             'PARTIAL_READY',
  FAILED_BUT_MANUAL_ALLOWED: 'FAILED_BUT_MANUAL_ALLOWED',
});

// Statuses where the Gate can open now (with or without suggestions).
export const PACKET_READY_TO_OPEN_STATUSES = Object.freeze([
  PACKET_PREPARATION_STATUS.READY,
  PACKET_PREPARATION_STATUS.PARTIAL_READY,
]);

/**
 * Compute Gate readiness from listPacketDocuments() output. Uses only
 * client-visible status fields (extractionStatus) — never candidate values.
 * Considers only READY documents (the promotable set).
 * @param {Array<{ status?: string, extractionStatus?: string }>} documents
 * @returns {string} a PACKET_PREPARATION_STATUS value
 */
export const computePacketReadiness = (documents = []) => {
  const considered = (documents || []).filter((d) => d.status === PACKET_DOCUMENT_STATUS.READY);
  if (considered.length === 0) return PACKET_PREPARATION_STATUS.READY; // nothing to prepare

  let ready = 0;
  let failed = 0;
  let active = 0;
  let notRun = 0;
  for (const d of considered) {
    const ex = d.extractionStatus || OCR_EXTRACTION_STATUS.NOT_RUN;
    if (ex === OCR_EXTRACTION_STATUS.COMPLETED || ex === OCR_EXTRACTION_STATUS.NEEDS_REVIEW) ready += 1;
    else if (ex === OCR_EXTRACTION_STATUS.FAILED) failed += 1;
    else if (ex === OCR_EXTRACTION_STATUS.QUEUED || ex === OCR_EXTRACTION_STATUS.PROCESSING) active += 1;
    else notRun += 1; // NOT_RUN
  }

  if (notRun > 0) return PACKET_PREPARATION_STATUS.NEEDS_PREPARATION;
  if (active > 0) return PACKET_PREPARATION_STATUS.PREPARING;
  if (failed === 0) return PACKET_PREPARATION_STATUS.READY;
  if (ready > 0) return PACKET_PREPARATION_STATUS.PARTIAL_READY;
  return PACKET_PREPARATION_STATUS.FAILED_BUT_MANUAL_ALLOWED;
};

// ── Phase 1.5: human document classification ─────────────────────────────────
// Stored on the packet document PHI manifest as `documentType` (separate from
// the upload/MIME `type` field). Ordered for the dropdown; UNKNOWN is the
// default and is treated as "not yet classified".
export const PACKET_DOCUMENT_CLASSIFICATIONS = Object.freeze([
  'REFERRAL_LETTER',
  'CLINICAL_NOTE',
  'LAB_RESULT',
  'IMAGING_REPORT',
  'INSURANCE_CARD',
  'MEDICATION_LIST',
  'DISCHARGE_SUMMARY',
  'CONSULT_NOTE',
  'OTHER',
  'UNKNOWN',
]);

export const DEFAULT_PACKET_DOCUMENT_CLASSIFICATION = 'UNKNOWN';

// User-facing labels for the new classification enum.
export const PACKET_DOCUMENT_CLASSIFICATION_LABELS = Object.freeze({
  REFERRAL_LETTER:   'Referral letter',
  CLINICAL_NOTE:     'Clinical note',
  LAB_RESULT:        'Lab result',
  IMAGING_REPORT:    'Imaging report',
  INSURANCE_CARD:    'Insurance card',
  MEDICATION_LIST:   'Medication list',
  DISCHARGE_SUMMARY: 'Discharge summary',
  CONSULT_NOTE:      'Consult note',
  OTHER:             'Other',
  UNKNOWN:           'Unknown',
});

// Backward-compatible neutral labels for the upload/MIME `type` enum — used as
// a fallback when documentType is UNKNOWN/missing (keeps "Document 1 · PDF").
export const PACKET_UPLOAD_TYPE_LABELS = Object.freeze({
  LABS:    'Lab',
  IMAGING: 'Imaging',
  PDF:     'PDF',
  PHOTO:   'Image',
  OTHER:   'Document',
});

/**
 * Resolve the best human label for a packet/referral document row.
 * Prefers the human classification (documentType) when it is a known,
 * non-UNKNOWN value; otherwise falls back to the neutral upload/MIME type label.
 * @param {{ documentType?: string, type?: string }} doc
 * @returns {string}
 */
export const resolveDocumentTypeLabel = (doc = {}) => {
  const dt = doc.documentType;
  if (dt && dt !== 'UNKNOWN' && PACKET_DOCUMENT_CLASSIFICATION_LABELS[dt]) {
    return PACKET_DOCUMENT_CLASSIFICATION_LABELS[dt];
  }
  return PACKET_UPLOAD_TYPE_LABELS[doc.type] || 'Document';
};

// ── Phase 2B-4: scan / type status for packet cards (status & counts ONLY) ────
// These derive compact, PHI-free labels from the Never-PHI document projection.
// NEVER returns candidate values, raw OCR text, filenames, or storage paths.

/**
 * Concise OCR scan status for a document row.
 * @param {{ extractionStatus?: string, extractionCandidateCount?: number }} doc
 * @returns {{ key: string, label: string, tone: 'ok'|'busy'|'error'|'muted' }}
 */
export const getDocumentScanStatus = (doc = {}) => {
  const ex = doc.extractionStatus || OCR_EXTRACTION_STATUS.NOT_RUN;
  const count = doc.extractionCandidateCount;
  switch (ex) {
    case OCR_EXTRACTION_STATUS.QUEUED:
    case OCR_EXTRACTION_STATUS.PROCESSING:
      return { key: 'scanning', label: 'Scanning…', tone: 'busy' };
    case OCR_EXTRACTION_STATUS.COMPLETED:
    case OCR_EXTRACTION_STATUS.NEEDS_REVIEW:
      return (count && count > 0)
        ? { key: 'scanned', label: `Scanned · ${count} suggestion${count === 1 ? '' : 's'}`, tone: 'ok' }
        : { key: 'scanned_empty', label: 'Scanned · 0 suggestions', tone: 'muted' };
    case OCR_EXTRACTION_STATUS.FAILED:
      return { key: 'failed', label: 'Scan failed', tone: 'error' };
    default:
      return { key: 'not_scanned', label: 'Not scanned', tone: 'muted' };
  }
};

/**
 * Packet-card scan readiness line (Inbox cards). Derives a compact, PHI-free
 * status from the Never-PHI packet root only — document counts, the trigger's
 * scan rollup (scannedDocumentCount / extractionFailedDocumentCount), and the
 * preparation summary. NEVER reads PHI or candidate values.
 *
 * "scanned" = OCR reached a terminal completed state (UNKNOWN type still counts).
 * 0 suggestions still counts as scanned — readiness never requires candidates.
 *
 * @param {object} packet  packet root projection
 * @returns {{ line: string, tone: 'ok'|'busy'|'warn'|'error'|'muted', ready: boolean }}
 */
export const getPacketScanReadiness = (packet = {}) => {
  const total = packet.documentCount || 0;
  const readyDocs = packet.readyDocumentCount || 0; // finalized/uploaded → scannable
  const scanned = packet.scannedDocumentCount != null
    ? packet.scannedDocumentCount
    : (packet.preparationReadyDocumentCount != null ? packet.preparationReadyDocumentCount : 0);
  const failed = packet.extractionFailedDocumentCount != null
    ? packet.extractionFailedDocumentCount
    : (packet.preparationFailedDocumentCount != null ? packet.preparationFailedDocumentCount : 0);
  const prep = packet.preparationStatus;
  const docWord = total === 1 ? 'document' : 'documents';
  const terminal = scanned + failed;

  if (readyDocs === 0) {
    return { line: `${total} ${docWord}`, tone: 'muted', ready: false };
  }
  if (scanned >= readyDocs) {
    return { line: `${total} ${docWord} · ${scanned} scanned · ready for referral`, tone: 'ok', ready: true };
  }
  if (failed > 0 && terminal >= readyDocs) {
    return { line: `${total} ${docWord} · ${scanned} scanned · scan needs review`, tone: 'warn', ready: true };
  }
  if (terminal > 0) {
    return { line: `${total} ${docWord} · ${scanned} scanned · scanning documents`, tone: 'busy', ready: false };
  }
  // Nothing terminal yet — fall back to the preparation summary if present.
  if (prep === PACKET_PREPARATION_STATUS.READY || prep === PACKET_PREPARATION_STATUS.PARTIAL_READY) {
    return { line: `${total} ${docWord} · ${scanned} scanned · ready for referral`, tone: 'ok', ready: true };
  }
  if (prep === PACKET_PREPARATION_STATUS.PREPARING) {
    return { line: `${total} ${docWord} · ${scanned} scanned · scanning documents`, tone: 'busy', ready: false };
  }
  if (prep === PACKET_PREPARATION_STATUS.FAILED_BUT_MANUAL_ALLOWED) {
    return { line: `${total} ${docWord} · scan failed · can continue manually`, tone: 'error', ready: true };
  }
  return { line: `${total} ${docWord} · 0 scanned · not scanned yet`, tone: 'muted', ready: false };
};

/**
 * Document type + provenance label (auto-detected / confirmed / review type).
 * @param {{ documentType?: string, documentTypeSource?: string, type?: string }} doc
 * @returns {{ typeLabel: string, source: string, tone: 'ok'|'auto'|'muted' }}
 */
export const getDocumentTypeProvenance = (doc = {}) => {
  const dt = doc.documentType;
  const known = dt && dt !== 'UNKNOWN' && PACKET_DOCUMENT_CLASSIFICATION_LABELS[dt];
  const source = doc.documentTypeSource;
  if (known && source === 'USER_SELECTED') {
    return { typeLabel: PACKET_DOCUMENT_CLASSIFICATION_LABELS[dt], source: 'Confirmed', tone: 'ok' };
  }
  if (known && source === 'AUTO_CLASSIFIED') {
    return { typeLabel: PACKET_DOCUMENT_CLASSIFICATION_LABELS[dt], source: 'Auto-detected', tone: 'auto' };
  }
  if (known) {
    return { typeLabel: PACKET_DOCUMENT_CLASSIFICATION_LABELS[dt], source: 'Confirmed', tone: 'ok' };
  }
  return { typeLabel: 'Unknown', source: 'Review type', tone: 'muted' };
};

// Canonical valid packet status transitions. Centralized to avoid drift.
export const VALID_PACKET_TRANSITIONS = Object.freeze({
  [PACKET_STATUS.INBOX]:             [PACKET_STATUS.DRAFTING_REFERRAL, PACKET_STATUS.FAILED],
  [PACKET_STATUS.DRAFTING_REFERRAL]: [PACKET_STATUS.COMPLETED, PACKET_STATUS.INBOX, PACKET_STATUS.FAILED],
  [PACKET_STATUS.FAILED]:            [PACKET_STATUS.INBOX],
  [PACKET_STATUS.COMPLETED]:         [], // terminal
});

// ── Firebase plumbing ────────────────────────────────────────────────────────

let _db = null;
let _functions = null;

const getDb = () => {
  if (!_db) _db = getFirestore(getApp());
  return _db;
};

const getFns = () => {
  if (!_functions) {
    const app = getFirebaseApp();
    if (app) _functions = getFunctions(app);
  }
  return _functions;
};

const call = async (name, data) => {
  const fns = getFns();
  if (!fns) throw new Error('Firebase Functions not initialized');
  const result = await httpsCallable(fns, name)(data);
  return result.data;
};

// ── Cloud Function wrappers ──────────────────────────────────────────────────

/**
 * Create a new pre-referral packet (status=INBOX, empty).
 * @returns {Promise<{ packetId: string }>}
 */
export const createPacket = () => call('createPacket', {});

/**
 * Create a signed PUT URL for a packet document.
 * @param {string} packetId
 * @param {{ mimeType: string, originalName: string, type: string }} opts
 * @returns {Promise<{ documentId: string, uploadUrl: string }>}
 */
export const createPacketDocumentUploadUrl = (packetId, { mimeType, originalName, type }) =>
  call('createPacketDocumentUploadUrl', { packetId, mimeType, originalName, type });

/**
 * Verify the binary landed in Storage and flip the document manifest to READY.
 * Must be called after the browser PUT succeeds.
 * @param {string} packetId
 * @param {string} documentId
 * @returns {Promise<{ success: true, sizeBytes: number }>}
 */
export const finalizePacketDocumentUpload = (packetId, documentId) =>
  call('finalizePacketDocumentUpload', { packetId, documentId });

/**
 * List packet documents (Never-PHI projection — no original filenames).
 * @param {string} packetId
 * @returns {Promise<{ documents: Array<{ documentId, status, type, documentType, mimeType, sizeBytes, createdAt }> }>}
 */
export const listPacketDocuments = (packetId) =>
  call('listPacketDocuments', { packetId });

/**
 * Create a short-lived signed GET URL to VIEW a packet document (Phase 1.5).
 * Server logs VIEW_PACKET_DOCUMENT_DOWNLOAD_URL. Never returns the filename.
 * @param {string} packetId
 * @param {string} documentId
 * @returns {Promise<{ downloadUrl: string, mimeType: string }>}
 */
export const createPacketDocumentDownloadUrl = (packetId, documentId) =>
  call('createPacketDocumentDownloadUrl', { packetId, documentId });

/**
 * Assign / update a packet document's human classification (Phase 1.5).
 * Writes documentType/typedByUid/typedAt/updatedAt to the PHI manifest only.
 * Throws on invalid type, PROMOTED doc, or disallowed packet status.
 * @param {string} packetId
 * @param {string} documentId
 * @param {string} type  one of PACKET_DOCUMENT_CLASSIFICATIONS
 * @returns {Promise<{ success: true, documentType: string }>}
 */
export const updatePacketDocumentType = (packetId, documentId, type) =>
  call('updatePacketDocumentType', { packetId, documentId, type });

/**
 * Request an OCR extraction run for a packet document (Phase 2A).
 * Creates a QUEUED job; a background trigger runs the provider (default no-op).
 * Status is read back via listPacketDocuments (manifest field), not here.
 * @param {string} packetId
 * @param {string} documentId
 * @returns {Promise<{ jobId: string }>}
 */
export const requestPacketDocumentExtraction = (packetId, documentId) =>
  call('requestPacketDocumentExtraction', { packetId, documentId });

// ── Phase 2B-2: patient/admin candidate review (gp_employee only) ────────────

export const CANDIDATE_REVIEW_ACTION = Object.freeze({
  ACCEPT: 'ACCEPT',
  EDIT:   'EDIT',
  REJECT: 'REJECT',
  CLEAR_REVIEW: 'CLEAR_REVIEW',
});

// Working/extraction statuses that are still actionable suggestions.
export const CANDIDATE_ACTIONABLE_STATUSES = Object.freeze(['SUGGESTED', 'LOW_CONFIDENCE', 'CONFLICTING']);
// Review outcomes.
export const CANDIDATE_REVIEW_STATUSES = Object.freeze(['ACCEPTED', 'EDITED', 'REJECTED']);

/**
 * List active patient/admin/insurance OCR candidates for a packet (gp_employee).
 * Returns review metadata + neutral document labels; never raw OCR / filenames /
 * storage paths. PHI access is logged server-side.
 * @param {string} packetId
 * @returns {Promise<{ candidates: Array }>}
 */
export const listPacketPatientAdminCandidates = (packetId) =>
  call('listPacketPatientAdminCandidates', { packetId });

/**
 * Record a GP review decision on a candidate (gp_employee). Does NOT write final
 * referral PHI — only the candidate review status.
 * @param {{ packetId, candidateId, action, editedValue?, rejectedReason? }} params
 * @returns {Promise<{ success: true, status: string }>}
 */
export const reviewPacketPatientAdminCandidate = ({ packetId, candidateId, action, editedValue, rejectedReason }) =>
  call('reviewPacketPatientAdminCandidate', { packetId, candidateId, action, editedValue, rejectedReason });

// Field source label for a candidate fieldKey (UI display only).
export const CANDIDATE_FIELD_LABELS = Object.freeze({
  'patient.name':          'Name',
  'patient.dateOfBirth':   'Date of birth',
  'patient.sex':           'Sex',
  'patient.identifier':    'Patient ID (MRN)',
  'contact.phone':         'Phone',
  'contact.email':         'Email',
  'contact.address':       'Address',
  'insurance.payer':       'Payer',
  'insurance.payerId':     'Payer ID',
  'insurance.memberId':    'Member ID',
  'insurance.groupNumber': 'Group number',
  'insurance.planType':    'Plan type',
  'insurance.planName':    'Plan name',
});

/**
 * Atomically flip packet INBOX → DRAFTING_REFERRAL and stamp draftingByUid.
 * Throws failed-precondition if packet is not in INBOX (race-safe).
 * @param {string} packetId
 * @returns {Promise<{ success: true }>}
 */
export const markPacketDrafting = (packetId) =>
  call('markPacketDrafting', { packetId });

/**
 * Prepare a packet for the Gate (Phase 2B-4, gp_employee only). Enqueues any
 * missing OCR/classification work and returns a readiness summary. Does not
 * block on long OCR — the client polls listPacketDocuments for readiness.
 * Writes only the Never-PHI readiness summary on the packet root (counts/status).
 * @param {string} packetId
 * @returns {Promise<{ preparationStatus: string, preparationDocumentCount: number,
 *   preparationReadyDocumentCount: number, preparationFailedDocumentCount: number,
 *   preparationCandidateCount: number, enqueued: number, configured: boolean }>}
 */
export const preparePreReferralPacketForGate = (packetId) =>
  call('preparePreReferralPacketForGate', { packetId });

/**
 * Phase 2C-3: match packet OCR against the SELECTED published Pack's evidence
 * maps, writing PHI clinical evidence candidates server-side. Returns a NON-PHI
 * counts-only summary (no snippets, values, or raw text). gp_employee only.
 *
 * NOTE: exposed for a future Gate-side evidence UI; NOT wired into any view in
 * this slice. Evidence found ≠ a clinical answer — nothing here changes the Gate.
 *
 * @param {{ packetId: string, specialtyId: string, packId: string, versionId: string }} params
 * @returns {Promise<{ status: 'COMPLETED'|'NO_EVIDENCE_MAP'|'NO_RAW_OCR'|'NO_MATCHES',
 *   candidateCount: number, fieldCandidateCount: number, optionCandidateCount: number,
 *   redFlagCandidateCount: number, attachmentCandidateCount: number }>}
 */
export const matchPacketEvidenceToPack = ({ packetId, specialtyId, packId, versionId }) =>
  call('matchPacketEvidenceToPack', { packetId, specialtyId, packId, versionId });

// Phase 2C-4 — Gate evidence chips. Read-only assistive pointers; never write
// clinical answers. gp_employee only; PHI snippets returned only for Gate display.
export const CLINICAL_EVIDENCE_REVIEW_ACTION = Object.freeze({ VIEW: 'VIEW', DISMISS: 'DISMISS' });

/**
 * List active, non-dismissed clinical evidence candidates for a packDocId (chips).
 * Response carries PHI snippets/values (gp-only, Gate display). Counts logged.
 * @param {{ packetId: string, packDocId: string }} params
 * @returns {Promise<{ candidates: Array }>}
 */
export const listPacketEvidenceCandidates = ({ packetId, packDocId }) =>
  call('listPacketEvidenceCandidates', { packetId, packDocId });

/**
 * Review a clinical evidence candidate — VIEW or DISMISS only. Never writes a
 * clinical answer / red flag / attachment. Returns status only.
 * @param {{ packetId: string, candidateId: string, action: 'VIEW'|'DISMISS' }} params
 * @returns {Promise<{ success: boolean, status: string }>}
 */
export const reviewPacketEvidenceCandidate = ({ packetId, candidateId, action }) =>
  call('reviewPacketEvidenceCandidate', { packetId, candidateId, action });

/**
 * Revert a packet that is DRAFTING_REFERRAL back to INBOX (user cancelled Gate
 * without submitting). Safe no-op if packet is already INBOX/COMPLETED/FAILED.
 * @param {string} packetId
 * @returns {Promise<{ success: true, status: string }>}
 */
export const releasePacketDraft = (packetId) =>
  call('releasePacketDraft', { packetId });

/**
 * Promote selected packet documents into the just-created referral. Idempotent
 * per documentId: retried calls skip already-PROMOTED docs. Each promoted doc
 * creates a referral PHI attachment manifest with provenance fields:
 *   source = 'PRE_REFERRAL_PACKET'
 *   sourcePacketId, sourcePacketDocumentId, promotedAt, promotedByUid
 *
 * Deterministic attachmentId = sha1(packetId + ':' + documentId + ':' + referralId).
 *
 * @param {{ packetId: string, referralId: string, packetDocumentIds: string[] }} params
 * @returns {Promise<{ promoted: string[], failed: string[] }>}
 */
export const promotePacketDocuments = ({ packetId, referralId, packetDocumentIds }) =>
  call('promotePacketDocuments', { packetId, referralId, packetDocumentIds });

/**
 * Atomically flip packet DRAFTING_REFERRAL → COMPLETED iff every selected doc
 * is PROMOTED. Stamps assignedReferralId / assignedAt / assignedByUid.
 * Throws failed-precondition if any selected doc is not PROMOTED.
 * @param {{ packetId: string, referralId: string, packetDocumentIds: string[] }} params
 * @returns {Promise<{ success: true, status: 'COMPLETED' }>}
 */
export const completePacket = ({ packetId, referralId, packetDocumentIds }) =>
  call('completePacket', { packetId, referralId, packetDocumentIds });

/**
 * Soft-delete an unsubmitted packet (status → DELETED). Server blocks deletion
 * of completed/assigned/promoted packets (throws failed-precondition
 * 'PACKET_ATTACHED_TO_REFERRAL'). No Storage binaries or PHI manifests are
 * touched. The Inbox/Completed subscriptions exclude DELETED automatically.
 * @param {string} packetId
 * @returns {Promise<{ success: true, status: 'DELETED' }>}
 */
export const deletePreReferralPacket = (packetId) =>
  call('deletePreReferralPacket', { packetId });

/**
 * Restore a soft-deleted packet (status DELETED → INBOX). Server blocks restore
 * of packets ever attached to a referral (throws failed-precondition
 * 'PACKET_ATTACHED_TO_REFERRAL'). Delete audit history is preserved.
 * @param {string} packetId
 * @returns {Promise<{ success: true, status: 'INBOX' }>}
 */
export const restorePreReferralPacket = (packetId) =>
  call('restorePreReferralPacket', { packetId });

// ── Firestore reads (Never-PHI root only) ────────────────────────────────────

const cleanDoc = (snap) => (snap.exists() ? { id: snap.id, ...snap.data() } : null);

/**
 * Fetch one packet root doc (Never-PHI).
 * Reads: tenants/{tenantId}/preReferralPackets/{packetId}
 * @param {string} tenantId
 * @param {string} packetId
 */
export const getPacket = async (tenantId, packetId) => {
  const snap = await getDoc(doc(getDb(), PATHS.preReferralPacket(tenantId, packetId)));
  return cleanDoc(snap);
};

/**
 * List packets in a given status (Inbox or Completed tab).
 * Reads: tenants/{tenantId}/preReferralPackets where status==<status>
 * @param {string} tenantId
 * @param {string} status  PACKET_STATUS value
 * @param {{ pageSize?: number }} options
 */
export const listPackets = async (tenantId, status, { pageSize = 50 } = {}) => {
  const ref = collection(getDb(), PATHS.col.preReferralPackets(tenantId));
  const q = query(
    ref,
    where('status', '==', status),
    orderBy('createdAt', 'desc'),
    limit(pageSize),
  );
  const snap = await getDocs(q);
  const rows = [];
  snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
  return rows;
};

/**
 * Subscribe to live packet list for a given status. Returns the unsubscribe fn.
 * Drives Inbox / Completed tab live-counters.
 */
export const subscribePackets = (tenantId, status, callback, { pageSize = 50 } = {}) => {
  const ref = collection(getDb(), PATHS.col.preReferralPackets(tenantId));
  const q = query(
    ref,
    where('status', '==', status),
    orderBy('createdAt', 'desc'),
    limit(pageSize),
  );
  return onSnapshot(q, (snap) => {
    const rows = [];
    snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
    callback(rows);
  });
};

/**
 * Subscribe to one packet root doc — used in Gate so the panel updates if
 * upload finishes after the user navigated in.
 */
export const subscribePacket = (tenantId, packetId, callback) => {
  const ref = doc(getDb(), PATHS.preReferralPacket(tenantId, packetId));
  return onSnapshot(ref, (snap) => callback(cleanDoc(snap)));
};
